import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import SelecteurProduction, {
  productionParDefaut,
} from '../../shared/components/SelecteurProduction';
import MargeTab from '../../shared/components/MargeTab';
import LimiteErreur from '../../shared/components/LimiteErreur';
import ParametresMargeModal from '../../shared/components/ParametresMargeModal';
import {
  CULTURES,
  RCP_OPTIONS, RCP_DEFAUT,
  ANNEES_ANCRAGE,
  EQUIPEMENTS,
  DEFAULT_EQUIP_PARAMS,
} from '../../shared/constants';

/** Seuls les maïs et le tournesol sont proposés sur la page marge. */
const CULTURES_MARGE = CULTURES.filter(
  (c) => c.famille === 'mais' || c.famille === 'tournesol'
);

/** Prix de la production par défaut (€/t) selon la famille de culture. */
const PRIX_VENTE_PAR_FAMILLE = { mais: 200, tournesol: 460 };

function familleDe(production) {
  return CULTURES_MARGE.find((c) => c.id === production?.id)?.famille;
}

function prixVenteParDefaut(production) {
  return PRIX_VENTE_PAR_FAMILLE[familleDe(production)] ?? 200;
}

/**
 * 2035-2050 / Eau et irrigation / Rentabilité irrigation.
 *
 * Reprend MargeTab (DiagoEau). Les réglages qui étaient dans la sidebar de
 * DiagoEau sont répartis : les plus courants restent dans la sidebar, les
 * paramètres détaillés (prix de l'eau et de l'électricité par année d'ancrage,
 * caractéristiques de l'équipement) passent par la modale "plus de paramètres".
 */
export default function RentabiliteIrrigation() {
  const { idExploit } = useOAD();

  const [production, setProduction] = React.useState(() =>
    productionParDefaut(CULTURES_MARGE)
  );
  const [rcp, setRcp] = React.useState(RCP_DEFAUT);

  const [prixVente, setPrixVente] = React.useState(() =>
    prixVenteParDefaut(production)
  );

  // Changement de culture : on remet le prix par défaut uniquement si la
  // famille change (maïs <-> tournesol), pour ne pas écraser un prix saisi
  // quand on passe d'un maïs à un autre.
  function changerProduction(nouvelle) {
    if (familleDe(nouvelle) !== familleDe(production)) {
      setPrixVente(prixVenteParDefaut(nouvelle));
    }
    setProduction(nouvelle);
  }
  const [fraisCultureHorsIrrigation, setFraisCultureHorsIrrigation] = React.useState(943);
  const [typeMarge, setTypeMarge] = React.useState('annuelle');
  const [parHa, setParHa] = React.useState(true);
  const [equipement, setEquipement] = React.useState('enrouleur');
  const [equipParams, setEquipParams] = React.useState(DEFAULT_EQUIP_PARAMS);
  const [lissage, setLissage] = React.useState(5);
  const [prixEau, setPrixEau] = React.useState(
    Object.fromEntries(ANNEES_ANCRAGE.map((a) => [a, 0.1]))
  );
  const [prixElec, setPrixElec] = React.useState(
    Object.fromEntries(ANNEES_ANCRAGE.map((a) => [a, 0.19]))
  );
  const [modaleOuverte, setModaleOuverte] = React.useState(false);

  const prixEauList = React.useMemo(
    () => ANNEES_ANCRAGE.map((a) => ({ annee_ancrage: a, c_eau: prixEau[a] ?? 0.1 })),
    [prixEau]
  );
  const prixElecList = React.useMemo(
    () => ANNEES_ANCRAGE.map((a) => ({ annee_ancrage: a, c_elec: prixElec[a] ?? 0.19 })),
    [prixElec]
  );

  const baseParams = React.useMemo(
    () => ({
      idExploitation: idExploit,
      type: production.type,
      idProduction: production.id,
      semis: production.semis,
      alimentation: production.alimentation,
      abreuvement: production.abreuvement,
      rcp,
    }),
    [idExploit, production, rcp]
  );

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="2035-2050"
        niveau2="eau-irrigation"
        niveau3="rentabilite-irrigation"
        idExploit={idExploit}
      >
        <SelecteurProduction
          valeur={production}
          onChange={changerProduction}
          cultures={CULTURES_MARGE}
          avecElevage={false}
        />

        <label className="sidebar-champ">
          <span className="sidebar-label">Prix de la production (€/t)</span>
          <input
            type="number"
            value={prixVente}
            onChange={(e) => setPrixVente(Number(e.target.value))}
          />
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Affichage</span>
          <select value={typeMarge} onChange={(e) => setTypeMarge(e.target.value)}>
            <option value="annuelle">Marge par année</option>
            <option value="cumulee">Marge cumulée</option>
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Équipement</span>
          <select value={equipement} onChange={(e) => setEquipement(e.target.value)}>
            {EQUIPEMENTS.map((eq) => (
              <option key={eq.value} value={eq.value}>
                {eq.label}
              </option>
            ))}
          </select>
        </label>

        <label className="sidebar-checkbox">
          <input
            type="checkbox"
            checked={parHa}
            onChange={(e) => setParHa(e.target.checked)}
          />
          <span>Ramener à l'hectare</span>
        </label>

        <button
          type="button"
          className="sidebar-bouton-parametres"
          onClick={() => setModaleOuverte(true)}
        >
          Plus de paramètres
        </button>

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-rentabilite"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
                // TRACC n'est pas disponible sur cette page.
                disabled={option.value === 'tracc'}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <LimiteErreur cle={`${production.id}-${rcp}-${equipement}`}>
        <section className="oad-card">
          <h2 className="tdb-section-titre">Rentabilité de l'irrigation</h2>
          <MargeTab
            baseParams={baseParams}
            prixVente={prixVente}
            fraisCultureHorsIrrigation={fraisCultureHorsIrrigation}
            equipement={equipement}
            selectedEquipParams={equipParams[equipement]}
            lissage={lissage}
            parHa={parHa}
            typeMarge={typeMarge}
            prixEauList={prixEauList}
            prixElecList={prixElecList}
          />
        </section>
        </LimiteErreur>
      </main>

      <ParametresMargeModal
        isOpen={modaleOuverte}
        onClose={() => setModaleOuverte(false)}
        equipement={equipement}
        equipParams={equipParams}
        setEquipParams={setEquipParams}
        prixEau={prixEau}
        setPrixEau={setPrixEau}
        prixElec={prixElec}
        setPrixElec={setPrixElec}
        lissage={lissage}
        setLissage={setLissage}
        fraisCultureHorsIrrigation={fraisCultureHorsIrrigation}
        setFraisCultureHorsIrrigation={setFraisCultureHorsIrrigation}
      />
    </div>
  );
}
